import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { cn } from '../../lib/classNameHelpers';
import { formatDollar, formatNumber, formatPercentage } from '../../lib/formatters';
import { OrderSide } from '../../types/generated';
import { ColoredMetric } from '../ColoredMetric/ColoredMetric';
import { Skeleton } from '../Skeleton/Skeleton';
import { PositionWithData } from './types';

export type PnlCardDisplayProps = {
  hidePnl: boolean;
  isLoading: boolean;
  backgroundUrl: string;
  position?: PositionWithData;
  cardRef?: React.RefObject<HTMLDivElement | null>;
  // We need this to make sure we generate the same image regardless of device width
  forExport?: boolean;
};

export function PnlCardDisplay({
  cardRef,
  hidePnl,
  position,
  isLoading,
  backgroundUrl,
  forExport = false,
}: PnlCardDisplayProps) {
  const isClosed = (position?.size ?? 0n) === 0n;
  const isLong = position?.side === OrderSide.BUY;

  const isAboveSm = useTailwindBreakpoint('sm');

  const etherealLink = window.location.origin;

  const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');

  return (
    <div
      ref={cardRef}
      className={cn('size-full flex flex-col', {
        'p-6': forExport,
        'p-2 sm:p-6': !forExport,
      })}
      style={{
        // Solid background color as fallback to prevent white lines
        backgroundColor: '#080809',
        // No background when loading so we don't get a flicker between red and green depending on P&L
        backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0) 100%)${isLoading ? '' : `, url('${backgroundUrl}')`}`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="flex justify-between">
        {isLoading ? (
          <Skeleton className="w-44" />
        ) : (
          <p
            className={cn({
              'font-semibold text-xl': forExport,
              'font-semibold sm:text-lg md:text-xl': !forExport,
            })}
          >
            {position?.displayTicker} |{' '}
            <span className={cn(isLong ? 'text-contentGreen' : 'text-contentRed')}>
              {isLong ? 'LONG' : 'SHORT'}
            </span>
          </p>
        )}
        <img src={isAboveSm ? '/logo.svg' : '/logo-small.svg'} alt="logo" className="h-6 w-auto" />
      </div>
      <div
        className={cn('flex flex-col space-y-1 overflow-hidden', {
          'mt-8': forExport,
          'mt-2 sm:mt-8 md:mt-10 lg:mt-8': !forExport,
        })}
      >
        {isLoading ? (
          <Skeleton className="my-2 h-9 w-72" />
        ) : (
          <ColoredMetric
            className={cn({
              'text-7xl font-bold': forExport,
              'text-5xl font-bold sm:text-6xl md:text-7xl': !forExport,
            })}
            value={position?.pnlPercentage}
            formatFn={(x) => formatPercentage(x, { signDisplay: 'exceptZero' })}
          />
        )}
        <div className="relative">
          <motion.div
            key="pnl"
            initial={false}
            custom={hidePnl}
            style={{ overflow: 'hidden' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            animate={hidePnl ? { opacity: 0, x: 40 } : { opacity: 1, x: 0 }}
          >
            {isLoading ? (
              <Skeleton className="h-7 w-24" />
            ) : (
              <ColoredMetric
                className={cn({
                  'text-2xl font-semibold': forExport,
                  'text-lg font-semibold sm:text-xl md:text-2xl': !forExport,
                })}
                value={position?.pnl}
                formatFn={(x) => formatDollar(x, { signDisplay: 'exceptZero' })}
              />
            )}
          </motion.div>
        </div>
      </div>
      <div
        className={cn('flex', {
          'gap-x-6 mt-6': forExport,
          'gap-x-4 sm:gap-x-6 my-4 sm:my-6': !forExport,
        })}
      >
        <div>
          <p
            className={cn('font-medium', {
              'text-base text-contentSecondary': forExport,
              'text-xs text-contentSecondary sm:text-sm md:text-base': !forExport,
            })}
          >
            Entry Price
          </p>
          {isLoading ? (
            <Skeleton className="h-5 w-20" />
          ) : (
            <p
              className={cn({
                'text-base': forExport,
                'text-xs sm:text-sm md:text-base': !forExport,
              })}
            >
              {formatNumber(position?.entryPrice, {
                maximumFractionDigits: position?.priceDecimals,
                minimumFractionDigits: position?.priceDecimals,
              })}
            </p>
          )}
        </div>
        <div>
          <p
            className={cn('font-medium', {
              'text-base text-contentSecondary': forExport,
              'text-xs text-contentSecondary sm:text-sm md:text-base': !forExport,
            })}
          >
            {isClosed ? 'Exit Price' : 'Current Price'}
          </p>
          {isLoading ? (
            <Skeleton className="h-5 w-20" />
          ) : (
            <p
              className={cn({
                'text-base': forExport,
                'text-xs sm:text-sm md:text-base': !forExport,
              })}
            >
              {formatNumber(position?.exitOrOraclePrice, {
                maximumFractionDigits: position?.priceDecimals,
                minimumFractionDigits: position?.priceDecimals,
              })}
            </p>
          )}
        </div>
      </div>
      <div className="mt-auto flex items-end justify-between">
        <div className="flex items-center gap-3">
          <div className="size-6 bg-white p-0.5 md:size-10">
            <QRCodeSVG
              className="size-full"
              value={etherealLink}
              bgColor="#000"
              fgColor="#ffffff"
              level="M"
            />
          </div>
          <div className="flex flex-col">
            <p
              className={cn('text-contentSecondary font-medium', {
                'text-base': forExport,
                'text-xs sm:text-sm md:text-base': !forExport,
              })}
            >
              Trade on Ethereal
            </p>
            <p
              className={cn({
                'text-base': forExport,
                'text-xs sm:text-sm md:text-base': !forExport,
              })}
            >
              {etherealLink}
            </p>
          </div>
        </div>
        <p
          className={cn('text-2xs', {
            'hidden sm:block': !forExport,
          })}
        >
          {timestamp}
        </p>
      </div>
    </div>
  );
}
